import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  UserCheck,
  BarChart3,
  AlertTriangle,
  AlertOctagon,
  Search,
  Download,
  Loader2,
  ChevronDown,
  ChevronUp,
  UserX,
  Users,
  BookOpen,
  CheckCircle,
  XCircle,
  ClipboardList,
  FileText,
} from 'lucide-react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  getAttendanceAnalytics,
  getStudentAttendance,
  getStudentAttendanceDetail,
  getClassAttendance,
  getAbsenceAlerts,
} from '@/services/adminAttendanceService';
import { getAdminGradeFilters } from '@/services/adminGradesService';
import type { AdminGradeFiltersResponse } from '@/types/admin-grades.types';
import { AttendanceExportModal } from '../components/attendance/AttendanceExportModal';
import { StudentReportModal } from '../components/attendance/StudentReportModal';
import type {
  AdminAttendanceFilters,
  StudentAttendanceRow,
  StudentAttendanceDetail,
  ClassAttendanceRow,
  AbsenceAlertRow,
  AttendanceAnalyticsSummary,
} from '@/types/admin-attendance.types';

type TabKey = 'students' | 'classes' | 'alerts';

interface TabDefinition {
  key: TabKey;
  label: string;
}

const TABS: TabDefinition[] = [
  { key: 'students', label: 'Par Étudiant' },
  { key: 'classes', label: 'Par Matière' },
  { key: 'alerts', label: 'Alertes' },
];

function getAbsenceRateColor(rate: number): string {
  if (rate > 20) return 'text-red-600';
  if (rate >= 10) return 'text-amber-600';
  return 'text-green-600';
}

function getAbsenceRateBgColor(rate: number): string {
  if (rate > 20) return 'bg-red-100 text-red-700';
  if (rate >= 10) return 'bg-amber-100 text-amber-700';
  return 'bg-green-100 text-green-700';
}

function getCumulativeAbsencesColor(count: number): string {
  if (count > 5) return 'text-red-600 font-semibold';
  if (count >= 3) return 'text-amber-600 font-medium';
  return 'text-green-600';
}

function getStatusBadge(status: 'NORMAL' | 'ALERT' | 'DANGER'): { label: string; className: string } {
  switch (status) {
    case 'NORMAL':
      return { label: 'Normal', className: 'bg-green-100 text-green-700' };
    case 'ALERT':
      return { label: 'Alerte', className: 'bg-amber-100 text-amber-700' };
    case 'DANGER':
      return { label: 'Danger', className: 'bg-red-100 text-red-700' };
  }
}

// ============================================================
// MultiSelectFilter - inline multi-select dropdown component
// ============================================================
interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  allLabel: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

function MultiSelectFilter({ label, allLabel, options, selected, onChange }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allSelected = selected.length === options.length && options.length > 0;

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const buttonLabel = selected.length === 0 ? allLabel : `${selected.length} sélectionnés`;

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left"
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </span>
          </label>
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export default function AttendanceDashboardPage() {
  // Permission check for manage/export actions
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.ATTENDANCE_MANAGE);

  // Filter data from backend
  const [filtersData, setFiltersData] = useState<AdminGradeFiltersResponse | null>(null);

  // Filter selections
  const [selectedCampusIds, setSelectedCampusIds] = useState<string[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');
  const [selectedSemesterNumbers, setSelectedSemesterNumbers] = useState<number[]>([]);
  const [selectedFiliereIds, setSelectedFiliereIds] = useState<string[]>([]);
  const [selectedNiveaux, setSelectedNiveaux] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('students');

  // Data states
  const [analytics, setAnalytics] = useState<AttendanceAnalyticsSummary | null>(null);
  const [studentRows, setStudentRows] = useState<StudentAttendanceRow[]>([]);
  const [classRows, setClassRows] = useState<ClassAttendanceRow[]>([]);
  const [alertRows, setAlertRows] = useState<AbsenceAlertRow[]>([]);

  // Expanded student detail
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null);
  const [studentDetail, setStudentDetail] = useState<StudentAttendanceDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Loading / Error
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Export modals
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [studentReportModal, setStudentReportModal] = useState<{
    open: boolean;
    studentId: string;
    studentName: string;
    studentCode: string;
  }>({ open: false, studentId: '', studentName: '', studentCode: '' });

  // Build filters object
  const buildFilters = useCallback((): AdminAttendanceFilters => {
    return {
      campusIds: selectedCampusIds.length > 0 ? selectedCampusIds : undefined,
      academicYearId: selectedAcademicYearId || undefined,
      semesterNumbers: selectedSemesterNumbers.length > 0 ? selectedSemesterNumbers : undefined,
      filiereIds: selectedFiliereIds.length > 0 ? selectedFiliereIds : undefined,
      niveaux: selectedNiveaux.length > 0 ? selectedNiveaux : undefined,
    };
  }, [selectedCampusIds, selectedAcademicYearId, selectedSemesterNumbers, selectedFiliereIds, selectedNiveaux]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilters = async () => {
      setFiltersLoading(true);
      try {
        const data = await getAdminGradeFilters();
        setFiltersData(data);

        // Set default: current academic year only
        const currentYear = data.academicYears.find((ay) => ay.isCurrent);
        if (currentYear) {
          setSelectedAcademicYearId(currentYear.id);
        } else if (data.academicYears.length > 0) {
          setSelectedAcademicYearId(data.academicYears[0].id);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erreur lors du chargement des filtres';
        setError(message);
      } finally {
        setFiltersLoading(false);
      }
    };
    loadFilters();
  }, []);

  // Load data when filters or tab change
  const loadData = useCallback(async () => {
    if (!selectedAcademicYearId) return;

    setDataLoading(true);
    setError(null);

    const filters = buildFilters();

    try {
      // Always load analytics
      const analyticsData = await getAttendanceAnalytics(filters);
      setAnalytics(analyticsData);

      // Load tab-specific data
      switch (activeTab) {
        case 'students': {
          const data = await getStudentAttendance(filters);
          // Sort by cumulativeAbsences descending
          data.sort((a, b) => b.cumulativeAbsences - a.cumulativeAbsences);
          setStudentRows(data);
          break;
        }
        case 'classes': {
          const data = await getClassAttendance(filters);
          // Sort by absenceRate descending (worst first)
          data.sort((a, b) => b.absenceRate - a.absenceRate);
          setClassRows(data);
          break;
        }
        case 'alerts': {
          const data = await getAbsenceAlerts(filters);
          // Sort by exceedsBy descending (most critical first)
          data.sort((a, b) => b.exceedsBy - a.exceedsBy);
          setAlertRows(data);
          break;
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement des données';
      setError(message);
    } finally {
      setDataLoading(false);
    }
  }, [selectedAcademicYearId, buildFilters, activeTab]);

  useEffect(() => {
    if (!filtersLoading && selectedAcademicYearId) {
      loadData();
    }
  }, [filtersLoading, loadData, selectedAcademicYearId]);

  // Close expanded row when tab changes
  useEffect(() => {
    setExpandedStudentId(null);
    setStudentDetail([]);
  }, [activeTab]);

  // Handle expanding a student row to show per-module detail
  const handleToggleStudentDetail = async (studentId: string) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
      setStudentDetail([]);
      return;
    }

    setExpandedStudentId(studentId);
    setDetailLoading(true);

    try {
      const filters = buildFilters();
      const detail = await getStudentAttendanceDetail(studentId, filters);
      setStudentDetail(detail);
    } catch (err: unknown) {
      console.error('Erreur chargement détail étudiant:', err);
      setStudentDetail([]);
    } finally {
      setDetailLoading(false);
    }
  };

  // Client-side search filtering for students
  const filteredStudentRows = useMemo(() => {
    if (!searchQuery.trim()) return studentRows;
    const q = searchQuery.toLowerCase();
    return studentRows.filter(
      (row) =>
        row.studentName.toLowerCase().includes(q) ||
        row.studentCode.toLowerCase().includes(q)
    );
  }, [studentRows, searchQuery]);

  // Client-side search filtering for alerts
  const filteredAlertRows = useMemo(() => {
    if (!searchQuery.trim()) return alertRows;
    const q = searchQuery.toLowerCase();
    return alertRows.filter(
      (row) =>
        row.studentName.toLowerCase().includes(q) ||
        row.studentCode.toLowerCase().includes(q)
    );
  }, [alertRows, searchQuery]);

  // Client-side search filtering for classes
  const filteredClassRows = useMemo(() => {
    if (!searchQuery.trim()) return classRows;
    const q = searchQuery.toLowerCase();
    return classRows.filter(
      (row) =>
        row.moduleElementName.toLowerCase().includes(q) ||
        row.moduleName.toLowerCase().includes(q) ||
        row.groupeName.toLowerCase().includes(q) ||
        row.professorName.toLowerCase().includes(q)
    );
  }, [classRows, searchQuery]);

  // Build filter summary labels for the export modal
  const filterSummary = useMemo(() => {
    const fd = filtersData;
    return {
      campusNames: selectedCampusIds.length > 0
        ? fd?.campuses?.filter((c) => selectedCampusIds.includes(c.id)).map((c) => c.name).join(', ') || 'Tous les campus'
        : 'Tous les campus',
      academicYearName: fd?.academicYears?.find((y) => y.id === selectedAcademicYearId)?.name || '-',
      semesterLabels: selectedSemesterNumbers.length > 0
        ? selectedSemesterNumbers.map((n) => `Semestre ${n}`).join(', ')
        : 'Tous les semestres',
      filiereNames: selectedFiliereIds.length > 0
        ? fd?.filieres?.filter((f) => selectedFiliereIds.includes(f.id)).map((f) => f.name).join(', ') || 'Toutes les filieres'
        : 'Toutes les filieres',
      niveauLabels: selectedNiveaux.length > 0
        ? selectedNiveaux.map((n) => `Niveau ${n}`).join(', ')
        : 'Tous les niveaux',
    };
  }, [filtersData, selectedCampusIds, selectedAcademicYearId, selectedSemesterNumbers, selectedFiliereIds, selectedNiveaux]);

  // Analytics cards definition
  const analyticsCards = [
    {
      label: 'Taux de Présence',
      value: `${analytics?.overallPresenceRate ?? 0}%`,
      icon: UserCheck,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: 'Moy. Absences/Classe',
      value: analytics?.avgAbsencesPerClass?.toFixed(1) ?? '-',
      icon: BarChart3,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Étudiants en Alerte',
      value: String(analytics?.totalAlertStudents ?? 0),
      icon: AlertTriangle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      label: 'Étudiants en Danger',
      value: String(analytics?.totalDangerStudents ?? 0),
      icon: AlertOctagon,
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
  ];

  // Full-page loading state
  if (filtersLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-red-100 rounded-xl">
            <UserX className="w-8 h-8 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Absences</h1>
            <p className="text-gray-500">
              Tableau de bord des absences et présences
            </p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={() => setExportModalOpen(true)}
            className="group flex items-center gap-2.5 px-5 py-2.5 text-white font-medium rounded-xl bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Download className="w-4.5 h-4.5 transition-transform duration-200 group-hover:-translate-y-0.5" />
            Exporter
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Campus - Multi-select */}
          <MultiSelectFilter
            label="Campus"
            allLabel="Tous les campus"
            options={(filtersData?.campuses ?? []).map((c) => ({ value: c.id, label: c.name }))}
            selected={selectedCampusIds}
            onChange={setSelectedCampusIds}
          />

          {/* Academic Year - Single select */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Année Académique</label>
            <select
              value={selectedAcademicYearId}
              onChange={(e) => setSelectedAcademicYearId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {filtersData?.academicYears.map((ay) => (
                <option key={ay.id} value={ay.id}>
                  {ay.name} {ay.isCurrent ? '(En cours)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Semester - Multi-select */}
          <MultiSelectFilter
            label="Semestre"
            allLabel="Tous les semestres"
            options={(filtersData?.semesters ?? []).map((s) => ({ value: String(s.number), label: s.label }))}
            selected={selectedSemesterNumbers.map(String)}
            onChange={(vals) => setSelectedSemesterNumbers(vals.map(Number))}
          />

          {/* Filière - Multi-select */}
          <MultiSelectFilter
            label="Filière"
            allLabel="Toutes les filières"
            options={(filtersData?.filieres ?? []).map((f) => ({ value: f.id, label: `${f.code} - ${f.name}` }))}
            selected={selectedFiliereIds}
            onChange={setSelectedFiliereIds}
          />

          {/* Niveau - Multi-select */}
          <MultiSelectFilter
            label="Niveau"
            allLabel="Tous les niveaux"
            options={(filtersData?.niveaux ?? []).map((n) => ({ value: String(n), label: `Niveau ${n}` }))}
            selected={selectedNiveaux.map(String)}
            onChange={(vals) => setSelectedNiveaux(vals.map(Number))}
          />
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {analyticsCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={`${card.bg} rounded-xl border border-gray-200 p-4 shadow-sm`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/60">
                  <Icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{card.label}</p>
                  <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Tab Bar + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="inline-flex border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {dataLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Tab: Students */}
            {activeTab === 'students' && (
              <StudentTab
                rows={filteredStudentRows}
                expandedStudentId={expandedStudentId}
                studentDetail={studentDetail}
                detailLoading={detailLoading}
                onToggleDetail={handleToggleStudentDetail}
                onRequestReport={(id, name, code) =>
                  setStudentReportModal({ open: true, studentId: id, studentName: name, studentCode: code })
                }
              />
            )}

            {/* Tab: Classes */}
            {activeTab === 'classes' && (
              <ClassTab rows={filteredClassRows} />
            )}

            {/* Tab: Alerts */}
            {activeTab === 'alerts' && (
              <AlertsTab rows={filteredAlertRows} />
            )}
          </>
        )}
      </div>

      {/* Export Modal */}
      <AttendanceExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        filters={buildFilters()}
        filterSummary={filterSummary}
      />

      {/* Student Report Modal */}
      <StudentReportModal
        isOpen={studentReportModal.open}
        onClose={() => setStudentReportModal({ open: false, studentId: '', studentName: '', studentCode: '' })}
        studentId={studentReportModal.studentId}
        studentName={studentReportModal.studentName}
        studentCode={studentReportModal.studentCode}
        filters={buildFilters()}
      />
    </div>
  );
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

// ---- Student Tab ----
interface StudentTabProps {
  rows: StudentAttendanceRow[];
  expandedStudentId: string | null;
  studentDetail: StudentAttendanceDetail[];
  detailLoading: boolean;
  onToggleDetail: (studentId: string) => void;
  onRequestReport: (studentId: string, studentName: string, studentCode: string) => void;
}

function StudentTab({ rows, expandedStudentId, studentDetail, detailLoading, onToggleDetail, onRequestReport }: StudentTabProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <Users className="w-12 h-12 mb-4 text-gray-300" />
        <p className="text-sm">Aucun étudiant trouvé</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiant</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filière</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Groupe</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Séances</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Présent</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Absent</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Retard</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Excusé</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Taux Absence</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cumulées</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
            <th className="px-3 py-3 w-10"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row) => {
            const isExpanded = expandedStudentId === row.studentId;
            const badge = getStatusBadge(row.status);

            return (
              <StudentRowGroup
                key={row.studentId}
                row={row}
                isExpanded={isExpanded}
                badge={badge}
                detailLoading={detailLoading}
                studentDetail={studentDetail}
                onToggle={() => onToggleDetail(row.studentId)}
                onRequestReport={() => onRequestReport(row.studentId, row.studentName, row.studentCode)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Single student row + expandable detail row
interface StudentRowGroupProps {
  row: StudentAttendanceRow;
  isExpanded: boolean;
  badge: { label: string; className: string };
  detailLoading: boolean;
  studentDetail: StudentAttendanceDetail[];
  onToggle: () => void;
  onRequestReport: () => void;
}

function StudentRowGroup({ row, isExpanded, badge, detailLoading, studentDetail, onToggle, onRequestReport }: StudentRowGroupProps) {
  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <div>
            <div className="text-sm font-medium text-gray-900">{row.studentName}</div>
            <div className="text-xs text-gray-500">{row.studentCode}</div>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">{row.filiereName}</td>
        <td className="px-3 py-3 text-center text-sm text-gray-700">{row.niveau}</td>
        <td className="px-3 py-3 text-sm text-gray-700">{row.groupeName}</td>
        <td className="px-3 py-3 text-center text-sm text-gray-700">{row.totalSessions}</td>
        <td className="px-3 py-3 text-center text-sm text-green-600 font-medium">{row.presentCount}</td>
        <td className="px-3 py-3 text-center text-sm text-red-600 font-medium">{row.absentCount}</td>
        <td className="px-3 py-3 text-center text-sm text-amber-600">{row.lateCount}</td>
        <td className="px-3 py-3 text-center text-sm text-blue-600">{row.excusedCount}</td>
        <td className="px-3 py-3 text-center">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAbsenceRateBgColor(row.absenceRate)}`}>
            {row.absenceRate.toFixed(1)}%
          </span>
        </td>
        <td className="px-3 py-3 text-center">
          <span className={`text-sm ${getCumulativeAbsencesColor(row.cumulativeAbsences)}`}>
            {row.cumulativeAbsences}
          </span>
        </td>
        <td className="px-3 py-3 text-center">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
            {badge.label}
          </span>
        </td>
        <td className="px-3 py-3 text-center">
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onRequestReport(); }}
              className="p-1 rounded hover:bg-indigo-50 transition-colors"
              title="Rapport individuel"
            >
              <FileText className="w-4 h-4 text-indigo-400 hover:text-indigo-600" />
            </button>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </td>
      </tr>

      {/* Expanded detail row */}
      {isExpanded && (
        <tr>
          <td colSpan={13} className="px-0 py-0">
            <div className="bg-blue-50 border-t border-b border-blue-100">
              {detailLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-blue-600">Chargement du détail...</span>
                </div>
              ) : studentDetail.length === 0 ? (
                <div className="flex items-center justify-center py-6 text-gray-500">
                  <BookOpen className="w-5 h-5 mr-2 text-gray-400" />
                  <span className="text-sm">Aucun détail disponible pour cet étudiant</span>
                </div>
              ) : (
                <div className="px-8 py-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3">Détail par matière</h4>
                  <table className="min-w-full divide-y divide-blue-200 bg-white rounded-lg overflow-hidden">
                    <thead className="bg-blue-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase">Matière</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Séances</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Présent</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Absent</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Retard</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Excusé</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Taux Absence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-blue-100">
                      {studentDetail.map((d) => (
                        <tr key={d.moduleElementId} className="hover:bg-blue-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{d.moduleElementName}</td>
                          <td className="px-3 py-2 text-center text-sm text-gray-700">{d.totalSessions}</td>
                          <td className="px-3 py-2 text-center text-sm text-green-600">{d.presentCount}</td>
                          <td className="px-3 py-2 text-center text-sm text-red-600">{d.absentCount}</td>
                          <td className="px-3 py-2 text-center text-sm text-amber-600">{d.lateCount}</td>
                          <td className="px-3 py-2 text-center text-sm text-blue-600">{d.excusedCount}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`text-sm font-medium ${getAbsenceRateColor(d.absenceRate)}`}>
                              {d.absenceRate.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---- Class Tab ----
interface ClassTabProps {
  rows: ClassAttendanceRow[];
}

function ClassTab({ rows }: ClassTabProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <ClipboardList className="w-12 h-12 mb-4 text-gray-300" />
        <p className="text-sm">Aucune classe trouvée</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matière</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Groupe</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filière</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Professeur</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Séances</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">% Absence</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Moy. Abs./Étud.</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Alerte</th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Nb Étud.</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={`${row.moduleElementId}-${row.groupeId}`} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">{row.moduleElementName}</div>
                  <div className="text-xs text-gray-500">{row.moduleName}</div>
                </div>
              </td>
              <td className="px-3 py-3 text-sm text-gray-700">{row.groupeName}</td>
              <td className="px-3 py-3 text-sm text-gray-700">{row.filiereName}</td>
              <td className="px-4 py-3 text-sm text-gray-700">{row.professorName}</td>
              <td className="px-3 py-3 text-center text-sm text-gray-700">{row.totalSessions}</td>
              <td className="px-3 py-3 text-center">
                <span className={`text-sm font-medium ${getAbsenceRateColor(row.absenceRate)}`}>
                  {row.absenceRate.toFixed(1)}%
                </span>
              </td>
              <td className="px-3 py-3 text-center text-sm text-gray-700">{row.avgAbsencesPerStudent.toFixed(1)}</td>
              <td className="px-3 py-3 text-center">
                {row.studentsAboveAlertThreshold > 0 ? (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    {row.studentsAboveAlertThreshold}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">0</span>
                )}
              </td>
              <td className="px-3 py-3 text-center text-sm text-gray-700">{row.totalStudents}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Alerts Tab ----
interface AlertsTabProps {
  rows: AbsenceAlertRow[];
}

function AlertsTab({ rows }: AlertsTabProps) {
  return (
    <div>
      {/* Alert badge */}
      {rows.length > 0 && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex items-center gap-2">
          <AlertOctagon className="w-5 h-5 text-red-600" />
          <span className="text-sm font-medium text-red-700">
            {rows.length} étudiant{rows.length > 1 ? 's' : ''} dépasse{rows.length > 1 ? 'nt' : ''} le seuil d&apos;absences autorisé
          </span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <CheckCircle className="w-12 h-12 mb-4 text-green-300" />
          <p className="text-sm">Aucune alerte d&apos;absence pour les filtres sélectionnés</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiant</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Groupe</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filière</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Absences Cumulées</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Seuil Max</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Dépassement</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rattrapage Créé</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row) => (
                <tr key={`${row.studentId}-${row.groupeId}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{row.studentName}</div>
                      <div className="text-xs text-gray-500">{row.studentCode}</div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700">{row.groupeName}</td>
                  <td className="px-3 py-3">
                    <div>
                      <div className="text-sm text-gray-700">{row.filiereCode}</div>
                      <div className="text-xs text-gray-500">{row.filiereName}</div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-700">{row.niveau}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-sm font-semibold text-red-600">{row.cumulativeAbsences}</span>
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-700">{row.maxAllowed}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      +{row.exceedsBy}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {row.hasRattrapage ? (
                      <CheckCircle className="w-5 h-5 text-green-500 inline-block" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400 inline-block" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
